import React from "react";
import useCollectionChange from "./useCollectionChange";

export default function useCollectionSubmission(settings, onSuccess) {
  const action = useCollectionChange(onSuccess);
  const rememberedRequest = React.useRef(null);
  const busy = React.useRef(false);
  const active = React.useRef(true);
  const [savingPreference, setSavingPreference] = React.useState(false);
  const [rememberFailed, setRememberFailed] = React.useState(false);
  React.useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  const saveAndRun = async () => {
    if (busy.current) return;
    busy.current = true;
    setSavingPreference(true);
    setRememberFailed(false);
    const request = rememberedRequest.current;
    try {
      const saved = await settings.save(request.preference);
      if (!active.current) return;
      if (!saved) {
        setRememberFailed(true);
        return;
      }
      // Saving the setting changes the current preference. Keep the confirmed target.
      rememberedRequest.current = null;
      await action.run(request.change);
    } finally {
      busy.current = false;
      if (active.current) setSavingPreference(false);
    }
  };
  return {
    ...action,
    pending: action.pending || savingPreference,
    rememberFailed,
    error: rememberFailed
      ? settings.saveError || new Error("Could not remember your selection.")
      : action.error,
    request: rememberedRequest.current?.change || action.request,
    run: (change, preference) => {
      if (busy.current || action.pending) return;
      if (preference) {
        rememberedRequest.current = {
          change: Object.freeze({ ...change }),
          preference,
        };
        return saveAndRun();
      }
      return action.run(change);
    },
    retry: () => (rememberedRequest.current ? saveAndRun() : action.retry()),
  };
}
